import * as actionTypes from './TreeActions';
import axios from 'axios';
import * as Sentry from '@sentry/react';
import i18n from 'i18next';
import Swal from 'sweetalert2';
import store from '../../../index';
import { backend_server_address } from '../../../backendServer';
import {
    GetTreeFailAction,
    GetTreeStartAction,
    GetTreeSuccessAction,
    MyThunkDispatch,
    MyThunkResult,
    StatusEntry,
} from './treeTypes';
import { mergeStatusEntries } from './fleetStatus';

export const getTreeStart = (): GetTreeStartAction => {
    return {
        type: actionTypes.GET_TREE_START,
        loadingTree: true,
    };
};

export const getTreeFail = (error: Error): GetTreeFailAction => {
    return {
        type: actionTypes.GET_TREE_FAIL,
        error: error,
        loadingTree: false,
    };
};

/**
 * The lifecycle fields sit on the node itself, alongside `id`/`type` — unlike the live status fields
 * (`health`, `running`, …), which are nested under `data`. Both levels are read so the value is found
 * wherever the serializer puts it.
 */
export const readLifecycleState = (node: any): string | undefined =>
    node?.lifecycle_state ?? node?.data?.lifecycle_state;

// Every MACHINE in a `/api/model/tree/` response, whatever depth it came back at.
const fetchedMachines = (tree: any): any[] => {
    const machines: any[] = [];
    const pending = [tree];
    while (pending.length > 0) {
        const node = pending.pop();
        if (node.type === 'MACHINE') machines.push(node);
        if (node.children) pending.push(...node.children);
    }
    return machines;
};

export const getTreeSuccess = (tree: any, node_id: number | null, silent = false): GetTreeSuccessAction => {
    let treeMap: any = {};
    let old_tree = store.getState().nodeTreeReducers.tree;
    if (!old_tree) old_tree = tree;
    else old_tree = JSON.parse(JSON.stringify(old_tree));
    let current = [old_tree];
    let next: any[] = [];
    while (current.length > 0) {
        for (let i in current) {
            if (current[i].id === tree.id) {
                for (let k in tree) {
                    current[i][k] = tree[k];
                }
            }
            treeMap[current[i].id] = current[i];
            if (current[i].children) next = next.concat(current[i].children);
        }
        current = next;
        next = [];
    }
    // Seed the live status slice from the tree's embedded machine status so it's populated at first
    // paint (the poll then refreshes it). This makes `status` the single source — consumers read it
    // with no fallback to node.data.
    //
    // Only machines THIS response carried are seeded. The merged tree also holds branches from
    // earlier fetches, whose `data` is the snapshot taken when they were loaded; polls since then
    // wrote to `status`, not to the tree. Seeding from those would push a subtree refresh of one
    // machine over the live status of every other machine in the fleet.
    const machineEntries: StatusEntry[] = fetchedMachines(tree).map((n: any) => {
        const entry: StatusEntry = {
            node_id: n.id,
            health: n.data?.health,
            health_score: n.data?.health_score,
            observability: n.data?.observability,
            running: n.data?.running,
            on_seconds_estimate: n.data?.on_seconds_estimate,
        };
        // `mergeStatusEntries` overlays by key presence, so an always-present `undefined` would
        // wipe the value a fleet_status poll had already set.
        const lifecycleState = readLifecycleState(n);
        if (lifecycleState !== undefined) entry.lifecycle_state = lifecycleState;
        return entry;
    });
    const status = mergeStatusEntries(store.getState().nodeTreeReducers.status ?? {}, machineEntries);
    return {
        type: actionTypes.GET_TREE_SUCCESS,
        tree: old_tree,
        // A silent refresh never raised the flag, so it must not lower it: a real tree load can be
        // in flight, and the dashboards loader keys off it. The reducer merges the action as-is, so
        // leaving the key out means "don't touch".
        ...(silent ? {} : { loadingTree: false }),
        treeMap: treeMap,
        treeMapLength: Object.keys(treeMap).length,
        status,
        statusUpdatedAt: new Date().toISOString(),
    };
};

/**
 * A silent refresh has no loader to fail into, and nothing else retries it: the ~5 min fleet poll
 * refreshes machine `status` but never the tree, and `selectNode` skips the fetch for a machine whose
 * children are already loaded. So a dropped refresh leaves the point and sensor condition on screen
 * stale for the rest of the session unless the user is told to reload.
 *
 * Reported to Sentry as well as shown: SweetAlert2 has no queue, so a success toast from the mutation
 * that asked for this refresh can land on top of this warning and hide it.
 */
const notifySilentRefreshFailed = (error: Error): void => {
    Sentry.captureException(error, { tags: { 'tree.refresh': 'silent' } });
    Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'warning',
        title: i18n.t('Could not refresh the equipment condition'),
        text: i18n.t('Reload the page'),
        showConfirmButton: false,
        timer: 6000,
        timerProgressBar: true,
    });
};

type GetTreeOptions = {
    /**
     * Refresh structure already on screen, in the background: `loadingTree` is left alone, so the
     * full-page dashboards loader (`platform/dashboards/index.tsx`) stays down and an in-place
     * mutation doesn't blank the view it just updated. A failure is surfaced by
     * `notifySilentRefreshFailed` instead of raising a tree-wide error over a mutation that succeeded.
     */
    silent?: boolean;
};

export const getTree = (
    node_id: number | null = null,
    max_depth: string | null = null,
    { silent = false }: GetTreeOptions = {},
): MyThunkResult<void> => {
    return (dispatch: MyThunkDispatch) => {
        if (!silent) dispatch(getTreeStart());
        let params: { node_id?: number; max_depth?: string } = {};
        if (node_id !== null) params['node_id'] = node_id;
        if (max_depth !== null) params['max_depth'] = max_depth;
        axios
            .get(backend_server_address + '/api/model/tree/', {
                withCredentials: true,
                params: params,
            })
            .then((res) => {
                dispatch(getTreeSuccess(res.data.data, node_id, silent));
            })
            .catch((error) => {
                if (silent) notifySilentRefreshFailed(error);
                else dispatch(getTreeFail(error));
            });
    };
};
